import React from "react";
import {
	addTimeToDate,
	Button,
	ClassNameProps,
	Column,
	dateTrunc,
	Grid,
	If,
	isSameTimeOrAfter,
	makeClassName,
	mappedArray,
	Modal,
	Row,
	StyleProps,
	useCount,
} from "@hex-insights/core";
import { Icon } from "../../../../Components/Icon";
import {
	NewsletterOrderField,
	OrderDirection,
	useAreViewed,
	useElementSize,
	useNewsletterDisplayListQuery,
} from "../../../../Utilities";
import { NewsletterCard } from "../Card";
import { NewslettersListModal } from "../ListModal";
import styles from "./styles.module.css";

export type NewsletterCarouselProps = Partial<ClassNameProps & StyleProps>;

export function NewsletterCarousel({ className, style }: NewsletterCarouselProps) {
	// round up to the next hour but then filter out newsletters that have not been released yet
	const [now] = React.useState(() => dateTrunc(addTimeToDate(new Date(), [1, "hour"]), "hour").toISOString()); // date trunc for improved caching
	const { data } = useNewsletterDisplayListQuery({
		variables: {
			filters: {
				releaseAtLTE: now,
			},
			order: { field: NewsletterOrderField.ReleaseAt, direction: OrderDirection.Desc },
			pagination: { first: 9 },
		},
	});

	const ids = React.useMemo(() => data?.newsletterConnection.edges.map((e) => e.node.id) ?? [], [data]);
	const viewMap = useAreViewed(ids);

	const newsletterEdges = React.useMemo(() => {
		if (!data) {
			return [];
		}

		return data.newsletterConnection.edges.filter(
			(e) => e.node.releaseAt && isSameTimeOrAfter(new Date(), e.node.releaseAt),
		);
	}, [data]);

	const ref = React.useRef<HTMLDivElement | null>(null);
	const { width } = useElementSize(ref);
	let displayNum = 1;
	if (width >= 750) {
		displayNum = 3;
	} else if (width >= 475) {
		displayNum = 2;
	}

	const numPages = Math.max(Math.ceil(newsletterEdges.length / displayNum), 1);
	const {
		count: displayIndex,
		increment: incrementDisplayIndex,
		decrement: decrementDisplayIndex,
	} = useCount(0, { mod: numPages });

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<div ref={ref} className={className} style={style}>
			<Column justify="spaced-start" verticalSpacing="0.5rem">
				<Grid tracks={displayNum} gap="1rem">
					{newsletterEdges.slice(displayIndex * displayNum, displayIndex * displayNum + displayNum).map((e) => (
						<NewsletterCard key={e.node.id} newsletter={e.node} variant="tile" isNew={!viewMap[e.node.id]} />
					))}
				</Grid>

				<div
					className={makeClassName(
						styles["newsletter-carousel__menu"],
						displayNum === 1 ? styles["newsletter-carousel__menu--column"] : "",
					)}
				>
					<If condition={displayNum > 1}>
						<Button variant="link" size="small" onClick={openModal} style={{ opacity: 0, pointerEvents: "none" }}>
							{/* for spacing */}
							View All Newsletters
						</Button>
					</If>

					<If condition={numPages > 1}>
						<Row justify="spaced-start" align="center" className={styles["newsletter-carousel__nav"]}>
							<Button variant="link" size="small" onClick={decrementDisplayIndex}>
								<Icon.CaretLeft size={18} className="block" />
							</Button>
							<Row justify="spaced-center">
								{mappedArray(numPages, (i) => (
									<div
										key={i}
										className={makeClassName(
											styles["newsletter-carousel__page-dot"],
											i === displayIndex ? styles["newsletter-carousel__page-dot--active"] : "",
										)}
									/>
								))}
							</Row>
							<Button variant="link" size="small" onClick={incrementDisplayIndex}>
								<Icon.CaretRight size={18} className="block" />
							</Button>
						</Row>
					</If>

					<Button variant="link" size="small" onClick={openModal}>
						View All Newsletters
					</Button>
				</div>

				<Modal.If condition={isModalOpen}>
					<NewslettersListModal newsletterEdges={newsletterEdges} viewMap={viewMap} onClose={closeModal} />
				</Modal.If>
			</Column>
		</div>
	);
}
